import React, { useState, useEffect } from "react";
import {
    Box,
    TextField,
    Button,
    Stack,
    Typography,
    Alert,
    Grid,
    IconButton,
    Checkbox,
    FormControlLabel
} from "@mui/material";
import { MenuItem } from "@mui/material";
import axios from "axios";
import DeleteIcon from "@mui/icons-material/Delete";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-python";
import "ace-builds/src-noconflict/theme-dracula";
import "ace-builds/src-noconflict/snippets/python";


const AddBrokerForm = ({ initialData, onSuccess }) => {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

    const [form, setForm] = useState({
        brokerName: "",
        brokerCode: "",
        iconUrl: "",
        description: "",
        connectorCode: "",
        instrumentCodeTemplate: "",
        instrument_url: "",
        attributes: [],
    });

    const [newAttribute, setNewAttribute] = useState({
        label: "",
        varName: "",
        dataType: "str",
        required: true,
        order: 1,
    });

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    // 👇 Fill form with existing broker data when editing
    useEffect(() => {
        if (initialData) {
            setForm(initialData);
        }
    }, [initialData]);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setError("");
        setSuccess("");
    };

    const handleAttrChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNewAttribute({
            ...newAttribute,
            [name]: type === "checkbox" ? checked : value,
        });
    };

    const addAttribute = () => {
        setForm({
            ...form,
            attributes: [...form.attributes, { ...newAttribute, order: form.attributes.length + 1 }],
        });
        setNewAttribute({ label: "", varName: "", dataType: "str", required: true, order: 1 });
    };

    const removeAttribute = (index) => {
        const updated = [...form.attributes];
        updated.splice(index, 1);
        setForm({ ...form, attributes: updated });
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem("token");
            if (form._id) {
                // 🔄 Update
                await axios.put(`${API_BASE_URL}/brokers/${form._id}`, form, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setSuccess("Broker updated successfully");
            } else {
                // ➕ Add
                await axios.post(`${API_BASE_URL}/brokers/add`, form, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setSuccess("Broker added successfully");
            }

            onSuccess();
            setForm({
                brokerName: "",
                brokerCode: "",
                iconUrl: "",
                description: "",
                connectorCode: "",
                instrumentCodeTemplate: "",
                instrument_url: "",
                attributes: [],
            });
        } catch (err) {
            setError(err.response?.data?.detail || "Failed to save broker");
        }
    };

    const pythonDataTypes = ["str", "int", "float", "bool", "list", "dict"];

    return (
        <Box component="form" onSubmit={handleSubmit} mb={3}>
            <Typography variant="h6">
                {form._id ? "Edit Broker" : "Add Broker"}
            </Typography>
            <Stack spacing={2} mt={2}>
                <TextField label="Broker Name" name="brokerName" value={form.brokerName} onChange={handleChange} required />
                <TextField label="Broker Code" name="brokerCode" value={form.brokerCode} onChange={handleChange} required />
                <TextField label="Icon URL" name="iconUrl" value={form.iconUrl} onChange={handleChange} />
                <TextField label="Description" name="description" value={form.description} onChange={handleChange} />
                <TextField label="Instrument URL" name="instrument_url" value={form.instrument_url || ""} onChange={handleChange} />

                <Typography variant="subtitle1">Connector Code</Typography>
                <AceEditor
                    mode="python"
                    theme="dracula"
                    name="connector-code"
                    value={form.connectorCode}
                    onChange={(value) => handleChange({ target: { name: "connectorCode", value } })}
                    fontSize={14}
                    width="100%"
                    height="400px"
                    style={{
                        backgroundColor: "#2f3136",
                        color: "#f8f8f2",
                        borderRadius: "4px"
                    }}
                    setOptions={{
                        useWorker: true,
                        tabSize: 4,
                        enableSnippets: true,
                        showLineNumbers: true,
                        highlightActiveLine: true,
                        enableBasicAutocompletion: true,
                        enableLiveAutocompletion: true
                    }}
                />

                <Typography variant="subtitle1">Instrument Code Template</Typography>
                <AceEditor
                    mode="python"
                    theme="dracula"
                    name="instrument-code"
                    value={form.instrumentCodeTemplate}
                    onChange={(value) => handleChange({ target: { name: "instrumentCodeTemplate", value } })}
                    fontSize={14}
                    width="100%"
                    height="400px"
                    style={{
                        backgroundColor: "#2f3136",
                        color: "#f8f8f2",
                        borderRadius: "4px"
                    }}
                    setOptions={{
                        useWorker: true,
                        tabSize: 4,
                        enableSnippets: true,
                        showLineNumbers: true,
                        highlightActiveLine: true,
                        enableBasicAutocompletion: true,
                        enableLiveAutocompletion: true
                    }}
                />

                <Typography variant="subtitle1">Attributes</Typography>
                {form.attributes.map((attr, index) => (
                    <Grid container spacing={1} key={index} alignItems="center">
                        <Grid item xs={3}><TextField value={attr.label} label="Label" size="small" disabled fullWidth /></Grid>
                        <Grid item xs={3}><TextField value={attr.varName} label="Var Name" size="small" disabled fullWidth /></Grid>
                        <Grid item xs={2}><TextField value={attr.dataType} label="Data Type" size="small" disabled fullWidth /></Grid>
                        <Grid item xs={2}><TextField value={String(attr.required)} label="Required" size="small" disabled fullWidth /></Grid>
                        <Grid item xs={1}><TextField value={attr.order} label="Order" size="small" disabled fullWidth /></Grid>
                        <Grid item xs={1}><IconButton onClick={() => removeAttribute(index)}><DeleteIcon /></IconButton></Grid>
                    </Grid>
                ))}

                <Box>
                    <Typography variant="subtitle2">Add New Attribute</Typography>
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs={3}><TextField name="label" label="Label" value={newAttribute.label} onChange={handleAttrChange} size="small" fullWidth /></Grid>
                        <Grid item xs={3}><TextField name="varName" label="Var Name" value={newAttribute.varName} onChange={handleAttrChange} size="small" fullWidth /></Grid>
                        <Grid item xs={2}>
                            <TextField
                                select
                                name="dataType"
                                label="Data Type"
                                value={newAttribute.dataType}
                                onChange={handleAttrChange}
                                size="small"
                                fullWidth
                            >
                                {pythonDataTypes.map((type) => (
                                    <MenuItem key={type} value={type}>{type}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={2}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="required"
                                        checked={newAttribute.required}
                                        onChange={handleAttrChange}
                                        size="small"
                                    />
                                }
                                label="Required"
                            />
                        </Grid>
                        <Grid item xs={2}><Button onClick={addAttribute} variant="outlined">Add</Button></Grid>
                    </Grid>
                </Box>

                {error && <Alert severity="error">{error}</Alert>}
                {success && <Alert severity="success">{success}</Alert>}

                <Button variant="contained" type="submit">
                    {form._id ? "Update Broker" : "Add Broker"}
                </Button>
            </Stack>
        </Box>
    );
};

export default AddBrokerForm;
